import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Pencil, X, Save, Trash2 } from 'lucide-react'
import api from '@/lib/axios'
import { documentsApi } from '@/api/documents'
import { fieldsApi, type ExtractionField } from '@/api/fields'
import { useAnnotations } from '@/hooks/useAnnotations'
import { PDFViewer } from '@/components/pdf/PDFViewer'
import { AnnotationLayer, type BoundingBox } from '@/components/pdf/AnnotationLayer'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'

interface DocumentViewerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  documentId: string
}

interface NewAnnotationDraft {
  bounding_box: BoundingBox
  field_id: string
  selected_text: string
  corrected_value: string
  annotation_note: string
}

export default function DocumentViewerDialog({
  open,
  onOpenChange,
  projectId,
  documentId,
}: DocumentViewerDialogProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [annotationMode, setAnnotationMode] = useState(false)
  const [draft, setDraft] = useState<NewAnnotationDraft | null>(null)
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 800, height: 1100 })
  const [isSaving, setIsSaving] = useState(false)

  const { data: document } = useQuery({
    queryKey: ['document', documentId],
    queryFn: () => documentsApi.get(documentId),
    enabled: open && !!documentId,
  })

  const { data: downloadUrlData } = useQuery({
    queryKey: ['document-download-url', documentId],
    queryFn: () => documentsApi.getDownloadUrl(documentId),
    enabled: open && !!documentId,
  })

  const { data: fields = [] } = useQuery({
    queryKey: ['fields', projectId],
    queryFn: () => fieldsApi.list(projectId),
    enabled: open && !!projectId,
  })

  const { annotations, createAnnotation, deleteAnnotation, isCreating } = useAnnotations(
    open ? documentId : null
  )

  const [extractingText, setExtractingText] = useState(false)

  const [draftPage, setDraftPage] = useState(currentPage)

  const handleAnnotationDrawn = useCallback(
    async (box: BoundingBox, page: number) => {
      setDraft({
        bounding_box: box,
        field_id: fields[0]?.id || '',
        selected_text: '',
        corrected_value: '',
        annotation_note: '',
      })
      setDraftPage(page)
      setAnnotationMode(false)

      // Extract text server-side using PyMuPDF
      if (documentId) {
        setExtractingText(true)
        try {
          const res = await api.post('/annotations/extract-text', {
            document_id: documentId,
            page_number: page,
            bounding_box: { x: box.x, y: box.y, width: box.width, height: box.height },
          })
          if (res.data?.text) {
            setDraft((d) => d ? { ...d, selected_text: res.data.text } : null)
          }
        } catch {
          // Silent fail — user can type manually
        } finally {
          setExtractingText(false)
        }
      }
    },
    [fields, documentId]
  )

  const handleSaveDraft = async () => {
    if (!draft || !documentId) return
    setIsSaving(true)
    try {
      await createAnnotation({
        document_id: documentId,
        page_number: draftPage,
        field_id: draft.field_id || null,
        bounding_box: draft.bounding_box,
        selected_text: draft.selected_text || null,
        corrected_value: draft.corrected_value || null,
        annotation_note: draft.annotation_note || null,
      })
      setDraft(null)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCanvasResize = useCallback((dims: { width: number; height: number }) => {
    setCanvasDimensions(dims)
  }, [])

  const pageAnnotations = annotations.filter((a) => a.page_number === currentPage)
  const allAnnotationsMapped = annotations.map((a) => ({
    id: a.id,
    field_id: a.field_id || '',
    field_name: fields.find((f: ExtractionField) => f.id === a.field_id)?.display_name,
    page_number: a.page_number,
    bounding_box: a.bounding_box || { x: 0, y: 0, width: 0, height: 0 },
    selected_text: a.selected_text || undefined,
  }))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
        <DialogHeader className="sr-only">
          <DialogTitle>{document?.original_filename || 'Document Viewer'}</DialogTitle>
          <DialogDescription>Annotate document fields</DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Left panel - PDF viewer (65% width) */}
          <div className="flex-[65] flex flex-col min-w-0 border-r border-gray-200">
            {/* Top bar */}
            <div className="flex items-center gap-3 px-4 py-2 bg-white border-b border-gray-200 shrink-0">
              <span className="text-sm font-medium text-gray-900 truncate">
                {document?.original_filename}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <Button
                  size="sm"
                  variant={annotationMode ? 'default' : 'outline'}
                  onClick={() => setAnnotationMode((v) => !v)}
                  className="flex items-center gap-1.5"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  {annotationMode ? 'Drawing...' : 'Annotate'}
                </Button>
              </div>
            </div>

            {/* PDF Canvas area */}
            <div style={{ flex: '1 1 0', minHeight: 0, overflow: 'hidden' }}>
              {downloadUrlData?.url ? (
                <PDFViewer
                  url={downloadUrlData.url}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  onTotalPages={setTotalPages}
                  onCanvasResize={handleCanvasResize}
                  renderPageOverlay={(page, dims) => (
                    <AnnotationLayer
                      canvasWidth={dims.width}
                      canvasHeight={dims.height}
                      currentPage={page}
                      annotations={allAnnotationsMapped}
                      annotationMode={annotationMode}
                      onAnnotationDrawn={(box) => handleAnnotationDrawn(box, page)}
                      draftBox={page === draftPage ? draft?.bounding_box || null : null}
                    />
                  )}
                  className="h-full"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              )}
            </div>
          </div>

          {/* Right panel - annotations (35% width) */}
          <div className="flex-[35] bg-white flex flex-col min-w-0">
            <div className="px-4 py-3 border-b border-gray-200 shrink-0">
              <h2 className="text-sm font-semibold text-gray-900">Annotations</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Page {currentPage} of {totalPages} -- {annotations.length} total annotation{annotations.length !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {/* Draft editor */}
              {draft && (
                <div className="border border-blue-300 rounded-lg p-3 bg-blue-50 space-y-3">
                  <p className="text-xs font-semibold text-blue-700">New Annotation</p>

                  <div>
                    <Label className="text-xs">Field</Label>
                    <Select
                      value={draft.field_id}
                      onValueChange={(v) => setDraft((d) => (d ? { ...d, field_id: v } : null))}
                    >
                      <SelectTrigger className="h-8 text-xs mt-1">
                        <SelectValue placeholder="Select field..." />
                      </SelectTrigger>
                      <SelectContent>
                        {fields.map((f: ExtractionField) => (
                          <SelectItem key={f.id} value={f.id}>
                            {f.display_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs">Page</Label>
                    <p className="text-xs text-gray-600 mt-1">Page {draftPage}</p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Raw Text</Label>
                      {extractingText ? (
                        <span className="text-[10px] text-blue-600 animate-pulse">Extracting...</span>
                      ) : draft.selected_text ? (
                        <span className="text-[10px] text-green-600">Auto-detected</span>
                      ) : null}
                    </div>
                    <Textarea
                      rows={2}
                      value={extractingText ? '' : draft.selected_text}
                      onChange={(e) =>
                        setDraft((d) => (d ? { ...d, selected_text: e.target.value } : null))
                      }
                      placeholder={extractingText ? 'Extracting text from document...' : 'Raw text from the selected region'}
                      className={`text-xs mt-1 ${extractingText ? 'bg-muted/50 text-muted-foreground' : ''}`}
                      disabled={extractingText}
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Helps the AI locate where to look in similar documents. Keep it as-is or refine it.
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Correct Extraction</Label>
                      <span className="text-[10px] text-gray-400">What the LLM should extract</span>
                    </div>
                    <Textarea
                      rows={2}
                      value={draft.corrected_value}
                      onChange={(e) =>
                        setDraft((d) => (d ? { ...d, corrected_value: e.target.value } : null))
                      }
                      placeholder="Enter the correct extraction value..."
                      className="text-xs mt-1"
                      autoFocus={!extractingText && !!draft.selected_text}
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Note (optional)</Label>
                    <Textarea
                      rows={1}
                      value={draft.annotation_note}
                      onChange={(e) =>
                        setDraft((d) => (d ? { ...d, annotation_note: e.target.value } : null))
                      }
                      placeholder="Additional context..."
                      className="text-xs mt-1"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 h-7 text-xs"
                      onClick={handleSaveDraft}
                      disabled={!draft.field_id || isSaving}
                    >
                      <Save className="h-3 w-3 mr-1" />
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => setDraft(null)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Existing annotations for current page */}
              {pageAnnotations.length === 0 && !draft ? (
                <div className="text-center py-8 text-gray-400">
                  <Pencil className="h-6 w-6 mx-auto mb-2 opacity-50" />
                  <p className="text-xs">No annotations on this page.</p>
                  <p className="text-xs mt-1">Click "Annotate" and draw a box to add one.</p>
                </div>
              ) : (
                pageAnnotations.map((ann) => {
                  const field = fields.find((f: ExtractionField) => f.id === ann.field_id)
                  return (
                    <div
                      key={ann.id}
                      className="border border-gray-200 rounded p-3 text-xs space-y-1"
                    >
                      <div className="flex items-center justify-between">
                        <Badge className="text-xs py-0 px-1.5">
                          {field?.display_name || 'Unlinked'}
                        </Badge>
                        <button
                          onClick={() => deleteAnnotation(ann.id)}
                          className="text-gray-400 hover:text-red-500"
                          title="Delete annotation"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                      {ann.selected_text && (
                        <p className="text-gray-500 bg-gray-50 px-2 py-1 rounded border border-gray-200 italic">
                          Raw: "{ann.selected_text}"
                        </p>
                      )}
                      {ann.corrected_value && (
                        <p className="text-gray-700 bg-green-50 px-2 py-1 rounded border border-green-200">
                          Extraction: "{ann.corrected_value}"
                        </p>
                      )}
                      {ann.annotation_note && (
                        <p className="text-gray-500">{ann.annotation_note}</p>
                      )}
                    </div>
                  )
                })
              )}

              {/* Annotations on other pages */}
              {annotations.filter((a) => a.page_number !== currentPage).length > 0 && (
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 mb-2">Other pages</p>
                  {annotations
                    .filter((a) => a.page_number !== currentPage)
                    .map((ann) => {
                      const field = fields.find((f: ExtractionField) => f.id === ann.field_id)
                      return (
                        <div
                          key={ann.id}
                          className="border border-gray-100 rounded p-2 text-xs space-y-1 mb-2 opacity-70"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <Badge variant="outline" className="text-xs py-0 px-1.5">
                                p.{ann.page_number}
                              </Badge>
                              <span className="text-gray-600">
                                {field?.display_name || 'Unlinked'}
                              </span>
                            </div>
                            <button
                              onClick={() => deleteAnnotation(ann.id)}
                              className="text-gray-400 hover:text-red-500"
                              title="Delete annotation"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                          {ann.selected_text && (
                            <p className="text-gray-500 italic truncate">"{ann.selected_text}"</p>
                          )}
                        </div>
                      )
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
